import type {
  AlbumSummary, Annotation, AssetCard, AssetDetail, DateBucket, DupeGroup, Filter, Me,
  Curated, Edits, Face, JobLog, ManagedShare, MergeSuggestion, PageRegions, PersonPage,
  PersonSummary,
  Precision, Proposal, ReelSummary, Rejection,
  Slice, Stats,
  StoredEdits,
  TimelineEntry, Tree,
} from "./types";
import * as fx from "./fixtures";

/**
 * The API client.
 *
 * Auth is a cookie set by `/api/auth/verify`, so every call sends credentials.
 * In development Vite proxies `/api` to `wrangler dev` on the same origin,
 * which is what keeps that cookie first-party — see vite.config.ts.
 */

/** `?demo=1` renders every view from `fixtures.ts` and touches no network. */
export const DEMO = new URLSearchParams(location.search).get("demo") === "1";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
  /** The permission layer's answer. Never shown; the control is hidden instead. */
  get denied(): boolean {
    return this.status === 403;
  }
  get unauthenticated(): boolean {
    return this.status === 401;
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: "include",
    headers: { Accept: "application/json", ...(init?.headers ?? {}) },
  });
  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!res.ok) {
    const why =
      body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : `request failed (${res.status})`;
    throw new ApiError(res.status, why);
  }
  return body as T;
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return req<T>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

/**
 * Filter to query string, in exactly the parameter names `parseFilter` in
 * cloud/src/index.ts reads. One encoder for every view, because the whole point
 * of the shared filter is that the same narrowing reaches each of them.
 */
export function filterParams(f: Filter, extra?: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  if (f.q) q.set("q", f.q);
  for (const p of f.people ?? []) q.append("person", p);
  if (f.from) q.set("from", f.from);
  if (f.to) q.set("to", f.to);
  if (f.albumId) q.set("album", f.albumId);
  if (f.kind) q.set("kind", f.kind);
  if (f.unnamed) q.set("unnamed", "1");
  if (f.facesPending) q.set("facesPending", "1");
  if (f.withStories) q.set("stories", "1");
  if (f.includeHidden) q.set("hidden", "1");
  for (const [k, v] of Object.entries(extra ?? {})) {
    if (v !== undefined && v !== "") q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

const lib = (id: string) => `/api/lib/${encodeURIComponent(id)}`;

export const api = {
  // --- account ----------------------------------------------------------
  me: (): Promise<Me> => (DEMO ? fx.me() : req<Me>("/api/me")),

  requestCode: (email: string): Promise<{ sent: boolean; code?: string }> =>
    DEMO ? fx.requestCode(email) : post("/api/auth/request-code", { email }),

  verify: (email: string, code: string): Promise<{ accountId: string; created: boolean }> =>
    DEMO ? fx.verify(email, code) : post("/api/auth/verify", { email, code }),

  signOut: (): Promise<unknown> => post("/api/auth/signout"),

  shares: (id: string): Promise<ManagedShare[]> =>
    DEMO ? fx.shares() : req<ManagedShare[]>(`${lib(id)}/shares`),

  inviteShare: (
    id: string,
    body: { email: string; scope: "album" | "archive"; permission: "view" | "edit"; albumId?: string },
  ): Promise<ManagedShare & { accessUrl?: string; code?: string }> =>
    DEMO ? fx.inviteShare(body) : post(`${lib(id)}/shares`, body),

  revokeShare: (id: string, shareId: string): Promise<unknown> =>
    DEMO ? fx.revokeShare(shareId) : post(`${lib(id)}/shares/${shareId}/revoke`, {}),

  // --- library reads ----------------------------------------------------
  stats: (id: string): Promise<Stats> =>
    DEMO ? fx.stats() : req<Stats>(`${lib(id)}/stats`),

  cards: (
    id: string,
    f: Filter,
    page: { limit?: number; cursor?: string; order?: "date" | "added" | "relevance" } = {},
  ): Promise<Slice<AssetCard>> =>
    DEMO
      ? fx.cards(f, page)
      : req<Slice<AssetCard>>(
          `${lib(id)}/cards${filterParams(f, {
            limit: page.limit ?? 60,
            cursor: page.cursor,
            order: page.order,
          })}`,
        ),

  dates: (id: string, f: Filter, grain: "month" | "year" | "decade"): Promise<DateBucket[]> =>
    DEMO ? fx.dates(f, grain) : req<DateBucket[]>(`${lib(id)}/dates${filterParams(f, { grain })}`),

  timeline: (id: string, f: Filter, grain: "year" | "decade"): Promise<TimelineEntry[]> =>
    DEMO
      ? fx.timeline(f, grain)
      : req<TimelineEntry[]>(`${lib(id)}/timeline${filterParams(f, { grain })}`),

  people: (id: string): Promise<PersonSummary[]> =>
    DEMO ? fx.people() : req<PersonSummary[]>(`${lib(id)}/people`),

  person: (id: string, who: string): Promise<PersonPage> =>
    DEMO ? fx.person(who) : req<PersonPage>(`${lib(id)}/person/${encodeURIComponent(who)}`),

  tree: (
    id: string,
    opts: { root?: string; depth?: number; direction?: "both" | "up" | "down"; photographed?: boolean },
  ): Promise<Tree> => {
    if (DEMO) return fx.tree(opts);
    const q = new URLSearchParams();
    if (opts.root) q.set("root", opts.root);
    if (opts.depth) q.set("depth", String(opts.depth));
    if (opts.direction) q.set("direction", opts.direction);
    if (opts.photographed) q.set("photographed", "1");
    return req<Tree>(`${lib(id)}/tree?${q}`);
  },

  albums: (id: string): Promise<AlbumSummary[]> =>
    DEMO ? fx.albums() : req<AlbumSummary[]>(`${lib(id)}/albums`),

  // --- library writes ---------------------------------------------------
  // Each of these is gated by a capability in `/stats`.`can`, and the client
  // does not render the control unless the capability is present — so a 403
  // here means a bug in the gating, not a user who tried something.

  assetDetail: (id: string, assetId: string): Promise<AssetDetail> =>
    DEMO
      ? Promise.resolve({ faces: [] })
      : req<AssetDetail>(`${lib(id)}/asset/${assetId}`),

  /** `asset.hide`. */
  setHidden: (id: string, assetId: string, hidden: boolean): Promise<unknown> =>
    DEMO ? fx.setHidden(assetId, hidden) : post(`${lib(id)}/hide`, { assetId, hidden }),

  /** `asset.metadata.edit`. Updates only fields explicitly supplied. */
  patchMetadata: (
    id: string,
    assetId: string,
    patch: { caption?: string | null; description?: string | null },
  ): Promise<unknown> =>
    DEMO ? fx.patchMetadata(assetId, patch) : post(`${lib(id)}/metadata`, { assetId, ...patch }),

  /** `asset.metadata.edit`. */
  setDate: (
    id: string,
    assetId: string,
    capturedAt: string | null,
    precision: Precision | null,
  ): Promise<unknown> =>
    DEMO
      ? fx.setDate(assetId, capturedAt, precision)
      : post(`${lib(id)}/date`, { assetId, capturedAt, precision }),

  /** `identity.name`. Names every photograph that face appears in at once. */
  nameIdentity: (id: string, identityId: string, name: string): Promise<unknown> =>
    DEMO ? fx.nameIdentity(identityId, name) : post(`${lib(id)}/name`, { identityId, name }),

  /** `album.create`. */
  createIdentity: (id: string, identityId: string): Promise<unknown> =>
    DEMO ? Promise.resolve({}) : post(`${lib(id)}/identities`, { id: identityId }),
  // --- edits the machine proposes ----------------------------------------
  proposals: (id: string): Promise<{ pending: number; items: Proposal[] }> =>
    DEMO
      ? Promise.resolve({ pending: 0, items: [] })
      : req<{ pending: number; items: Proposal[] }>(`${lib(id)}/proposals`),

  runProposals: (id: string): Promise<{ queued: number }> =>
    DEMO ? Promise.resolve({ queued: 0 }) : post(`${lib(id)}/proposals/run`, {}),

  decideProposal: (id: string, assetId: string, take: boolean): Promise<{ applied: boolean }> =>
    DEMO
      ? Promise.resolve({ applied: false })
      : post(`${lib(id)}/proposals`, { assetId, take }),

  // --- "is this the same person?" ----------------------------------------
  suggestions: (id: string): Promise<{ items: MergeSuggestion[] }> =>
    DEMO
      ? Promise.resolve({ items: [] })
      : req<{ items: MergeSuggestion[] }>(`${lib(id)}/suggestions`),

  decidedPairs: (id: string): Promise<{ pairs: string[] }> =>
    DEMO ? Promise.resolve({ pairs: [] }) : req<{ pairs: string[] }>(`${lib(id)}/decided`),

  suggestPairs: (
    id: string,
    pairs: Array<{ a: string; b: string; similarity: number }>,
  ): Promise<{ suggested: number }> =>
    DEMO ? Promise.resolve({ suggested: 0 }) : post(`${lib(id)}/suggestions`, { pairs }),

  decideSuggestion: (
    id: string,
    a: string,
    b: string,
    state: "merged" | "rejected",
  ): Promise<unknown> =>
    DEMO ? Promise.resolve({}) : post(`${lib(id)}/suggestions`, { decide: { a, b, state } }),

  // --- choosing what goes in a reel ---------------------------------------
  curate: (
    id: string,
    target: number,
    balance: "decade" | "person" | "chronological" | "quality" = "decade",
    people?: string[],
  ): Promise<Curated> =>
    DEMO
      ? Promise.resolve({ picks: [], decades: [] })
      : post(`${lib(id)}/curate`, { target, balance, people }),

  /** Where to fetch a short preview render from. */
  reelPreviewUrl: (id: string): string => `${lib(id)}/reels/preview`,

  // --- reels --------------------------------------------------------------
  faces: (id: string): Promise<{ fonts: Face[] }> =>
    DEMO
      ? Promise.resolve({ fonts: [] })
      : req<{ fonts: Face[] }>(`${lib(id)}/fonts`),

  reels: (id: string): Promise<{ reels: ReelSummary[] }> =>
    DEMO ? Promise.resolve({ reels: [] }) : req<{ reels: ReelSummary[] }>(`${lib(id)}/reels`),

  makeReel: (
    id: string,
    name: string,
    items: string[],
    style?: Record<string, unknown>,
  ): Promise<{ reelId: string }> =>
    DEMO
      ? Promise.resolve({ reelId: "" })
      : post(`${lib(id)}/reels`, { name, items, style }),

  deleteReel: (id: string, reelId: string): Promise<unknown> =>
    DEMO ? Promise.resolve({}) : post(`${lib(id)}/reels/delete/${reelId}`, {}),

  /** Where to play a finished reel from. */
  reelVideoUrl: (id: string, reelId: string): string =>
    `${lib(id)}/reels/video/${reelId}`,

  // --- what the server has been doing -----------------------------------
  jobs: (id: string): Promise<JobLog> =>
    DEMO
      ? Promise.resolve({ running: 0, failed: 0, recent: [] })
      : req<JobLog>(`${lib(id)}/jobs`),

  trimJobs: (id: string): Promise<{ removed: number }> =>
    DEMO ? Promise.resolve({ removed: 0 }) : post(`${lib(id)}/jobs`, {}),

  // --- the same picture twice -------------------------------------------
  dupes: (id: string): Promise<{ groups: DupeGroup[]; hashed: number }> =>
    DEMO
      ? Promise.resolve({ groups: [], hashed: 0 })
      : req<{ groups: DupeGroup[]; hashed: number }>(`${lib(id)}/dupes`),

  decideDupe: (
    id: string,
    a: string,
    b: string,
    verdict: "distinct" | "kept",
    keep?: string,
  ): Promise<unknown> =>
    DEMO ? Promise.resolve({}) : post(`${lib(id)}/dupes`, { a, b, verdict, keep }),

  rehash: (id: string): Promise<{ queued: number }> =>
    DEMO ? Promise.resolve({ queued: 0 }) : post(`${lib(id)}/rehash`, {}),

  // --- pages of photographs ---------------------------------------------
  pageRegions: (id: string, assetId: string): Promise<PageRegions> =>
    DEMO
      ? Promise.resolve({ regions: [], reviewedAt: null, children: 0 })
      : req<PageRegions>(`${lib(id)}/page/${assetId}`),

  extractPage: (
    id: string,
    assetId: string,
    regions: Array<{
      x: number;
      y: number;
      w: number;
      h: number;
      /** Four corners, when the print is not square to the page. */
      quad?: {
        tl: [number, number];
        tr: [number, number];
        br: [number, number];
        bl: [number, number];
      };
    }>,
    hidePage = true,
  ): Promise<{ created: number; failed: number[] }> =>
    DEMO
      ? Promise.resolve({ created: 0, failed: [] })
      : post(`${lib(id)}/page/${assetId}/extract`, { regions, hidePage }),

  keepPageWhole: (id: string, assetId: string): Promise<unknown> =>
    DEMO ? Promise.resolve({}) : post(`${lib(id)}/page/${assetId}/keep`, {}),

  // --- albums, beyond making one ----------------------------------------
  updateAlbum: (
    id: string,
    albumId: string,
    patch: { name?: string; description?: string | null; coverAsset?: string | null },
  ): Promise<unknown> =>
    DEMO ? Promise.resolve({}) : post(`${lib(id)}/albums/${albumId}/update`, patch),

  deleteAlbum: (id: string, albumId: string): Promise<{ items: number }> =>
    DEMO ? Promise.resolve({ items: 0 }) : post(`${lib(id)}/albums/${albumId}/delete`, {}),

  reorderAlbum: (id: string, albumId: string, assetIds: string[]): Promise<unknown> =>
    DEMO ? Promise.resolve({}) : post(`${lib(id)}/albums/${albumId}/reorder`, { assetIds }),

  // --- correcting the people --------------------------------------------
  rejections: (id: string): Promise<Rejection[]> =>
    DEMO ? Promise.resolve([]) : req<Rejection[]>(`${lib(id)}/rejections`),

  mergeIdentities: (
    id: string,
    into: string,
    from: string[],
  ): Promise<{ moved: number; removed: number }> =>
    DEMO
      ? Promise.resolve({ moved: 0, removed: 0 })
      : post(`${lib(id)}/merge`, { into, from }),

  /**
   * `identity.face.confirm`. One face on one photograph, given to one person.
   *
   * `null` unassigns it — "I do not know who this is", which is not the same
   * claim as `rejectFaces`'s "this is not her" and deliberately teaches the
   * matcher nothing.
   */
  assignFace: (
    id: string,
    assetId: string,
    faceId: string,
    identityId: string | null,
  ): Promise<{ identityId: string | null; previous: string | null }> =>
    DEMO
      ? Promise.resolve({ identityId, previous: null })
      : (post(`${lib(id)}/assign`, { assetId, faceId, identityId }) as Promise<{
          identityId: string | null;
          previous: string | null;
        }>),

  rejectFaces: (
    id: string,
    identityId: string,
    faceIds: string[],
    source: "user" | "cascade" = "user",
  ): Promise<{ rejected: number }> =>
    DEMO
      ? Promise.resolve({ rejected: 0 })
      : post(`${lib(id)}/reject`, { identityId, faceIds, source }),

  setStranger: (id: string, identityId: string, stranger = true): Promise<unknown> =>
    DEMO ? Promise.resolve({}) : post(`${lib(id)}/stranger`, { identityId, stranger }),

  forgetIdentity: (id: string, identityId: string): Promise<{ faces: number }> =>
    DEMO ? Promise.resolve({ faces: 0 }) : post(`${lib(id)}/forget`, { identityId }),

  // --- the darkroom -----------------------------------------------------
  getEdits: (id: string, assetId: string): Promise<StoredEdits> =>
    DEMO
      ? Promise.resolve({ edits: null, editedKey: null, colorizedKey: null })
      : req<StoredEdits>(`${lib(id)}/edits/${assetId}`),

  saveEdits: (id: string, assetId: string, edits: Edits | null): Promise<unknown> =>
    DEMO ? Promise.resolve({}) : post(`${lib(id)}/edits/${assetId}`, { edits }),

  autoEdits: (id: string, assetId: string, base: Edits): Promise<{ proposed: Edits }> =>
    DEMO
      ? Promise.resolve({ proposed: base })
      : post(`${lib(id)}/edits/${assetId}/auto`, { base }),

  renderEdits: (id: string, assetId: string, edits: Edits): Promise<{ key: string }> =>
    DEMO
      ? Promise.resolve({ key: "" })
      : post(`${lib(id)}/edits/${assetId}/render`, { edits }),

  colouriseAsset: (id: string, assetId: string): Promise<{ key: string }> =>
    DEMO
      ? Promise.resolve({ key: "" })
      : post(`${lib(id)}/edits/${assetId}/colourise`, {}),

  resetEdits: (id: string, assetId: string): Promise<unknown> =>
    DEMO ? Promise.resolve({}) : post(`${lib(id)}/edits/${assetId}/reset`, {}),

  reap: (id: string): Promise<{ removed: number }> =>
    DEMO ? Promise.resolve({ removed: 0 }) : post(`${lib(id)}/reap`, {}),
  addCrop: (
    id: string,
    body: { id: string; identityId: string; cropKey: string; quality?: number; sourceAsset?: string },
  ): Promise<unknown> => (DEMO ? Promise.resolve({}) : post(`${lib(id)}/crops`, body)),
  putFaces: (id: string, assetId: string, faces: unknown[]): Promise<unknown> =>
    DEMO ? Promise.resolve({}) : post(`${lib(id)}/faces`, { assetId, faces }),
  queueAnalysis: (id: string, assetIds: string[]): Promise<{ queued: number }> =>
    DEMO
      ? Promise.resolve({ queued: 0 })
      : (post(`${lib(id)}/analyse`, { assetIds }) as Promise<{ queued: number }>),
  setPersonCover: (id: string, identityId: string, cropId: string | null): Promise<unknown> =>
    DEMO ? Promise.resolve({}) : post(`${lib(id)}/cover`, { identityId, cropId }),
  addToAlbum: (id: string, albumId: string, assetIds: string[]): Promise<unknown> =>
    DEMO ? Promise.resolve({}) : post(`${lib(id)}/albums/${albumId}/items`, { assetIds }),
  createAlbum: (id: string, name: string, description?: string): Promise<unknown> =>
    DEMO ? fx.createAlbum(name, description) : post(`${lib(id)}/albums`, { name, description }),

  /**
   * Period annotations, fetched alongside `/timeline`.
   *
   * `/timeline` derives its buckets from photographs, so a period story in a
   * decade holding no photographs produces no bucket and the story is invisible
   * — which defeats the point of `subject_kind='period'`. The client asks for
   * the journal too and splices the orphans back in. See views/timeline.ts.
   */
  journal: (id: string, kinds?: string[]): Promise<Annotation[]> => {
    if (DEMO) return fx.journal();
    const q = new URLSearchParams();
    for (const k of kinds ?? []) q.append("kind", k);
    const s = q.toString();
    return req<Annotation[]>(`${lib(id)}/journal${s ? `?${s}` : ""}`);
  },
};

/**
 * Originals and derivatives. Content-addressed, so cacheable forever.
 *
 * Takes either a bare content hash or the R2 key that holds it — `blobs/…`,
 * `crops/…`, whatever a later prefix turns out to be — and strips the prefix
 * here, because the route is keyed by the hash alone.
 *
 * That stripping used to be the caller's job, which meant four call sites each
 * doing their own version of it and two more that did not know they had to.
 * Both of those rendered a broken image: the person cover strip and the face
 * picker asked for `/api/blob/blobs%2F<hash>`, got a 404, and drew the
 * browser's torn-page icon in the middle of somebody's family. Nothing logged,
 * because a 404 on an <img> is not an error anybody hears about.
 *
 * Returns null rather than a URL for anything that is not a hash once the
 * prefix is off, so a key of an unexpected shape draws a placeholder the
 * caller controls instead of a broken image.
 */
/**
 * An edited or colourised photograph.
 *
 * Not `blobUrl`: a render is derived rather than content-addressed, it is
 * replaced whenever the settings change, and it has no `blob_refs` row — so
 * the blob route 404s it. Which it did, silently, because `blobUrl` correctly
 * refuses a key that is not a hash and the darkroom then had nothing to show
 * and nothing to report.
 *
 * `at` is the render's own key, which carries a timestamp. It is in the URL
 * only to bust the browser cache: the route reads whichever rendering is
 * current, and a stale URL would otherwise keep showing the previous one.
 */
export function renditionUrl(
  libraryId: string,
  assetId: string,
  kind: "edited" | "colorized",
  at?: string | null,
): string {
  const q = new URLSearchParams({ kind });
  if (at) q.set("v", at);
  return `${lib(libraryId)}/rendition/${assetId}?${q}`;
}

/** The newest visible form of a photograph, from edits down to its original. */
export function assetImageUrl(libraryId: string, asset: AssetCard): string | null {
  if (asset.editedKey) return renditionUrl(libraryId, asset.id, "edited", asset.editedKey);
  if (asset.colorizedKey) {
    return renditionUrl(libraryId, asset.id, "colorized", asset.colorizedKey);
  }
  return blobUrl(asset.proxyKey ?? asset.contentHash);
}

export function blobUrl(key: string | null | undefined): string | null {
  if (!key) return null;
  const hash = key.slice(key.lastIndexOf("/") + 1);
  if (!/^[0-9a-f]{16,64}$/.test(hash)) return null;
  if (DEMO) return fx.placeholderImage(hash);
  return `/api/blob/${hash}`;
}

/**
 * A face crop, if it is fetchable.
 *
 * The same rule as `blobUrl` — crops are ordinary content-addressed blobs, and
 * the desktop writes them under `crops/` while the browser's face pass sends
 * them through `/upload/:hash` and gets `blobs/`. Kept as its own name because
 * a null here means "draw a monogram", which is a decision about people rather
 * than about images, and the call sites read better for saying so.
 */
export function cropUrl(cropKey: string | null | undefined): string | null {
  return blobUrl(cropKey);
}

/** The live-update socket for one library. */
export function liveUrl(libraryId: string): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}${lib(libraryId)}/live`;
}
